/**
 * Frontend logging utility
 * Sends logs to backend and provides console logging
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

class Logger {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.batchLogs = [];
    this.batchTimeout = null;
    this.batchSize = 10;
    this.batchInterval = 5000; // 5 seconds
  }

  /**
   * Send log to backend
   */
  async sendToBackend(level, message, metadata = {}) {
    // DISABLED: Backend logging endpoint not available
    // Keep logs in console only for now
    return;
  }

  /**
   * Schedule batch send
   */
  scheduleBatchSend() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.flushLogs();
    }, this.batchInterval);
  }

  /**
   * Flush all batched logs
   */
  async flushLogs() {
    if (this.batchLogs.length === 0) return;

    const logsToSend = [...this.batchLogs];
    this.batchLogs = [];

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    try {
      // Send each log (could be optimized to send as batch)
      for (const log of logsToSend) {
        await axios.post(`${API}/logs/frontend`, log, {
          // Don't use auth token for logging to avoid circular errors
          headers: { 'Content-Type': 'application/json' },
          transformRequest: [(data) => JSON.stringify(data)]
        });
      }
    } catch (error) {
      console.error('Failed to flush logs:', error);
    }
  }

  /**
   * Log info message
   */
  info(message, metadata = {}) {
    console.info(`[INFO] ${message}`, metadata);
    if (this.isProduction) {
      this.sendToBackend('info', message, metadata);
    }
  }

  /**
   * Log warning message
   */
  warning(message, metadata = {}) {
    console.warn(`[WARNING] ${message}`, metadata);
    this.sendToBackend('warning', message, metadata);
  }

  /**
   * Log error message
   */
  error(message, error = null, metadata = {}) {
    console.error(`[ERROR] ${message}`, error, metadata);
    
    const errorData = {
      ...metadata,
      error_stack: error ? error.stack : null,
      error_message: error ? error.message : null
    };
    
    this.sendToBackend('error', message, errorData);
  }

  /**
   * Log user action
   */
  logAction(action, details = {}) {
    const message = `User action: ${action}`;
    console.log(`[ACTION] ${message}`, details);
    
    if (this.isProduction) {
      this.sendToBackend('info', message, {
        user_action: action,
        ...details
      });
    }
  }

  /**
   * Log API call
   */
  logApiCall(method, endpoint, duration, status) {
    const message = `${method} ${endpoint} - ${status} (${duration}ms)`;
    console.log(`[API] ${message}`);
    
    if (this.isProduction) {
      this.sendToBackend('info', message, {
        api_method: method,
        api_endpoint: endpoint,
        api_duration_ms: duration,
        api_status: status
      });
    }
  }
}

// Create singleton instance
const logger = new Logger();

// Setup global error handlers
window.addEventListener('error', (event) => {
  logger.error('Uncaught error', event.error, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection', event.reason, {
    promise: event.promise
  });
});

export default logger;
